// Central registry of calculator tools — drives Featured Tools section, nav, sitemap.
import {
  Calculator,
  RefreshCw,
  Wallet,
  ShieldCheck,
  Car,
  BarChart3,
  TrendingUp,
  CalendarDays,
  Receipt,
  Percent,
  Scale,
  Gauge,
  Wrench,
  SlidersHorizontal,
} from "lucide-react";

export const TOOLS = [
  {
    slug: "auto-loan-calculator",
    title: "Auto Loan Calculator",
    short: "Loan Calculator",
    description: "Get your real monthly payment and total interest in seconds.",
    keyword: "auto loan calculator",
    Icon: Calculator,
    // Iter-196.R (M10) — Inline preview shown on /calculators cards.
    // Demonstrates the calculator's actual output shape so visitors
    // know what they'll get BEFORE clicking. Replaces the audit's
    // "no inline preview" gap with a lightweight text-based example.
    preview: {
      inputs: "$25,000 · 60mo · 7.5%",
      outputLabel: "Monthly payment",
      outputValue: "$501",
    },
  },
  {
    slug: "auto-loan-refinance-calculator",
    title: "Auto Refinance Savings Calculator",
    short: "Refinance Calculator",
    description: "See how much you save by refinancing your current car loan.",
    keyword: "auto refinance calculator",
    Icon: RefreshCw,
    preview: {
      inputs: "9.5% → 6.5% APR",
      outputLabel: "Monthly savings",
      outputValue: "$42",
    },
  },
  {
    slug: "car-affordability-calculator",
    title: "Car Affordability Calculator",
    short: "Affordability",
    description: "How much car can you really afford based on your income?",
    keyword: "how much car can I afford",
    Icon: Wallet,
    preview: {
      inputs: "$80k income · 10% rule",
      outputLabel: "Affordable price",
      outputValue: "~$28,000",
    },
  },
  {
    slug: "total-cost-of-ownership-calculator",
    title: "Total Car Ownership Cost Calculator",
    short: "Total Ownership Cost",
    description: "The true monthly + annual cost across depreciation, fuel, insurance, maintenance, and tires.",
    keyword: "total car ownership cost calculator",
    Icon: BarChart3,
    preview: {
      inputs: "5-yr ownership · all-in",
      outputLabel: "True cost",
      outputValue: "~$48,000",
    },
  },
  {
    slug: "auto-insurance-cost-estimator",
    title: "Insurance Cost Estimator",
    short: "Insurance Estimate",
    description: "Estimate your annual auto insurance premium in 30 seconds.",
    keyword: "auto insurance cost estimator",
    Icon: ShieldCheck,
    preview: {
      inputs: "ZIP · driver age · vehicle",
      outputLabel: "Annual premium",
      outputValue: "~$1,420",
    },
  },
  {
    slug: "auto-insurance-increase-calculator",
    title: "Auto Insurance Increase Calculator",
    short: "Insurance Increase",
    description: "See how much your premium went up — and whether it may be worth comparing.",
    keyword: "auto insurance increase calculator",
    Icon: TrendingUp,
    preview: {
      inputs: "$1,440 → $1,680/yr",
      outputLabel: "Annual delta",
      outputValue: "+$240",
    },
  },
  {
    slug: "trade-in-value-estimator",
    title: "Trade-In Value Estimator",
    short: "Trade-In Value",
    description: "Ballpark your trade-in value before walking into the dealer.",
    keyword: "trade in value estimator",
    Icon: Car,
    preview: {
      inputs: "2020 RAV4 · 45k mi",
      outputLabel: "Trade-in value",
      outputValue: "~$22,800",
    },
  },
  // Iter-196.T — 5 new calculators per audit (Lease vs Buy, Bi-weekly
  // payoff, Title-tax-fee, Total interest paid, Insurance premium
  // predictor). All follow the same CalculatorPage scaffolding +
  // PlainSummary + ResultTile pattern as the existing 7.
  {
    slug: "lease-vs-buy-calculator",
    title: "Lease vs. Buy Calculator",
    short: "Lease vs Buy",
    description: "Compare the 5-year real cost of leasing vs financing the same car.",
    keyword: "lease vs buy calculator",
    Icon: Scale,
    preview: {
      inputs: "$38k SUV · 12k mi/yr",
      outputLabel: "5-yr winner",
      outputValue: "Buy by $4,200",
    },
  },
  {
    slug: "bi-weekly-payment-calculator",
    title: "Bi-Weekly Payment Calculator",
    short: "Bi-Weekly",
    description: "See interest saved + months cut off by paying bi-weekly instead of monthly.",
    keyword: "bi-weekly payment calculator",
    Icon: CalendarDays,
    preview: {
      inputs: "$28k · 7.5% · 60mo",
      outputLabel: "Interest saved",
      outputValue: "~$1,180",
    },
  },
  {
    slug: "title-tax-fee-calculator",
    title: "Title, Tax & Fee Calculator",
    short: "Out-the-Door Price",
    description: "Estimate the real out-the-door price with sales tax, title, registration, and dealer fees.",
    keyword: "title tax fee calculator",
    Icon: Receipt,
    preview: {
      inputs: "$32k vehicle · TX",
      outputLabel: "Out-the-door",
      outputValue: "~$34,800",
    },
  },
  {
    slug: "total-interest-calculator",
    title: "Total Interest Paid Calculator",
    short: "Total Interest",
    description: "Compare 48 vs 60 vs 72 vs 84-month terms — see how much interest you actually pay.",
    keyword: "total interest paid calculator",
    Icon: Percent,
    preview: {
      inputs: "$32k · 7.5% APR",
      outputLabel: "84 vs 48mo delta",
      outputValue: "+$3,860",
    },
  },
  {
    slug: "insurance-premium-predictor",
    title: "Insurance Premium Predictor",
    short: "Premium Predictor",
    description: "Predict your annual auto insurance premium based on age, credit, record, and state.",
    keyword: "auto insurance premium predictor",
    Icon: Gauge,
    preview: {
      inputs: "Age 30 · clean · 100/300/100",
      outputLabel: "Annual premium",
      outputValue: "~$1,680",
    },
  },
  // Iter-196.CC — Warranty break-even calculator. Closes the
  // warranty-vertical calculator gap identified in the iter-196.BB
  // topical-authority audit. Returns Likely Worth It / Borderline /
  // Likely Not Worth It verdict based on reliability tier, current
  // mileage, plan price, term, and deductible.
  {
    slug: "extended-warranty-worth-it-calculator",
    title: "Is an Extended Warranty Worth It Calculator",
    short: "Warranty Worth-It",
    description: "Run the break-even math on any extended warranty quote — see if a typical buyer recovers more than they pay.",
    keyword: "is extended warranty worth it calculator",
    Icon: Wrench,
    preview: {
      inputs: "65k mi · Avg tier · $2,800/5yr",
      outputLabel: "Net break-even",
      outputValue: "-$430",
    },
  },
  // Iter-466 — Warranty Price Checker: match a dealer quote's terms to direct providers.
  {
    slug: "warranty-price-checker",
    title: "Warranty Price Checker",
    path: "/warranty-price-checker",
    short: "Warranty Price Check",
    description: "Enter your dealer's warranty quote and coverage — see which direct providers publish matching plans, then get exact quotes.",
    keyword: "extended warranty price checker",
    Icon: Wrench,
    preview: {
      inputs: "$3,200 · 5 yr · 150k mi",
      outputLabel: "Dealer quote / yr",
      outputValue: "$640",
    },
  },
  // Iter-418 — Deductible break-even. Lives under the /calculators
  // namespace (its own standalone URL) rather than the legacy
  // top-level `<slug>-calculator` pattern, hence the explicit `path`.
  {
    slug: "deductible-break-even",
    path: "/calculators/deductible-break-even",
    title: "Deductible Break-Even Calculator",
    short: "Deductible Break-Even",
    description: "See how many claim-free years a $1,000 deductible needs to beat a $500 one at your premium savings.",
    keyword: "500 vs 1000 deductible calculator",
    Icon: SlidersHorizontal,
    preview: {
      inputs: "$500 → $1,000 · $120/yr saved",
      outputLabel: "Break-even",
      outputValue: "4.2 yrs",
    },
  },
];

// Canonical URL for a tool. Most tools live at the top level
// (`/auto-loan-calculator`); newer ones declare an explicit `path`
// under the /calculators namespace.
export const toolPath = (tool) => tool.path || `/${tool.slug}`;

export const findTool = (slug) => TOOLS.find((t) => t.slug === slug);
